import { requestGet } from '@/common/request';
import { message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import batchedUpdates from '@/common/utils/batched-updates';

interface Query {
  channelId: string;
}

export enum JumpType {
  norefresh = 3,
  refresh = 2,
  newtab = 1,
}

export enum InjectType {
  native = 1,
  micro = 2,
  iframe = 3,
  link = 4,
}

export interface MenuItem {
  id: string;
  menuId: string;
  viewKey: string;
  injectType: InjectType; // 0 内部实现 1 micro 2 iframe
  path: string;

  jumpType: JumpType; // jumpType 跳转形式 0: 内部跳转， 1: url跳转 2 新开tab
  url: string;
  jumpUrl: string;
  microName: string;

  name: string; // 指用于标识，前端不用
  children?: MenuItem[];
}

interface Result {
  list?: MenuItem[];
  content?: MenuItem;
}

export default function queryGetMenu(query: Query): Promise<RequestResult<Result>> {
  return requestGet('/platform/getMenu', query).then((res) => {
    if (res.ret !== '0') return res;

    const innerRes = { ...res } as RequestResult<Result>;

    if (!innerRes.data) return innerRes;

    // 存在列表
    if (innerRes.data.list) {
      innerRes.data.list = res.data.list.map((item: any) => {
        return {
          ...item,
          menuId: item.viewKey,
        };
      });
    }

    // 定制频道内容，通常content存在list则不存在，即使存在也会被忽略
    if (innerRes.data.content) {
      innerRes.data.content = {
        ...innerRes.data.content,
        menuId: innerRes.data.content.viewKey,
        // content中的injectType 是list中的值+100
        injectType: innerRes.data.content.injectType - 100,
      };
    }

    return innerRes;
  });
}

// 路由是否内部实现
export function isNative({ injectType }: MenuItem) {
  return injectType === InjectType.native || injectType === InjectType.micro || injectType === InjectType.iframe;
}

// 路由是否是连接
export function isLink({ injectType }: MenuItem) {
  return injectType === InjectType.link;
}

const initialData = { list: [] };

export function useQueryGetMenu(query: Query) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<Result>();

  useEffect(() => {
    let didMount = true;
    if (!query) return;
    setLoading(true);
    const { channelId } = query;

    queryGetMenu(query)
      .then((res) => {
        if (!didMount) return;

        const { ret, data } = res || {};
        if (ret !== '0') {
          message.error('网络异常请稍候~');
          batchedUpdates(() => {
            setLoading(false);
            setState(initialData);
          });
          return;
        } else {
          batchedUpdates(() => {
            // @ts-ignore
            setState(data);
            setLoading(false);
          });
        }
      })
      .catch((ex) => {
        batchedUpdates(() => {
          setLoading(false);
          setState(initialData);
        });
        console.error(ex);
      });

    return () => {
      didMount = false;
    };
  }, [query]);

  const data = useMemo(() => {
    return { loading, data: state };
  }, [loading, state]);

  return [data];
}
